import { Navigate, useParams } from "react-router-dom";
import { Check } from "lucide-react";
import { SERVICES } from "../data/services";
import { Seo } from "../components/Seo";
import { MaskedLine, Reveal } from "../components/Reveal";
import { CTABanner } from "../components/sections/CTABanner";
import { VendorMarquee } from "../components/sections/VendorStack";
import { SITE_URL } from "../data/site";

export default function ServicePage() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/" replace />;

  const path = `/services/${service.slug}`;
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.name,
    name: `${service.name} — Sempresera`,
    description: service.metaDescription,
    url: `${SITE_URL}${path}`,
    provider: { "@type": "Organization", name: "Sempresera", url: `${SITE_URL}/`, foundingDate: "2015" },
    areaServed: { "@type": "Country", name: "United States" },
  };
  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: service.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <Seo title={service.metaTitle} description={service.metaDescription} path={path} jsonLd={[serviceLd, faqLd]} />

      <section className="relative overflow-hidden" data-testid="service-hero">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-5 pb-20 pt-36 md:grid-cols-12 md:px-8 md:pt-44">
          <div className="md:col-span-7">
            <MaskedLine delay={0.1}>
              <span className="eyebrow">Sempresera Services</span>
            </MaskedLine>
            <h1 className="mt-6 font-display text-4xl font-black leading-[1.02] tracking-tighter text-white md:text-6xl">
              <MaskedLine delay={0.25}>{service.name}</MaskedLine>
            </h1>
            <MaskedLine delay={0.4}>
              <p className="mt-4 font-display text-xl font-light text-brand md:text-2xl">{service.short}</p>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <p className="mt-8 max-w-xl text-base leading-relaxed text-[#C9D2DE]">{service.intro}</p>
            </MaskedLine>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.3}>
              <div className="img-bluewash clip-edge relative mx-auto aspect-square w-full max-w-md overflow-hidden">
                <img
                  src={service.image}
                  alt={service.imageAlt}
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <VendorMarquee />

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32" data-testid="service-included">
        <Reveal>
          <p className="eyebrow">What's included</p>
          <h2 className="mt-4 max-w-2xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-white md:text-5xl">
            All of it. <span className="text-brand">Standard.</span>
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden border border-white/10 bg-white/10 md:grid-cols-2">
          {service.included.map((item, i) => (
            <Reveal key={item} delay={Math.min(i * 0.05, 0.4)}>
              <div className="flex h-full items-start gap-4 bg-ink p-6">
                <Check size={18} className="mt-0.5 shrink-0 text-brand" strokeWidth={2.5} />
                <p className="text-sm leading-relaxed text-zinc-300">{item}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/5 bg-panel/60" data-testid="service-faq">
        <div className="mx-auto max-w-7xl px-5 py-24 md:px-8 md:py-32">
          <Reveal>
            <p className="eyebrow">Questions</p>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-white md:text-5xl">
              Asked and answered.
            </h2>
          </Reveal>
          <div className="mt-14 max-w-3xl space-y-5">
            {service.faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.08}>
                <details className="group glass p-6 md:p-8" data-testid={`faq-item-${i}`}>
                  <summary className="cursor-pointer list-none font-display text-lg font-bold text-white transition-colors duration-200 group-open:text-brand">
                    {f.q}
                  </summary>
                  <p className="mt-4 text-sm leading-relaxed text-[#C9D2DE]">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
